/**
 * Store Zustand pour gérer les listeners du bouton retour Android
 * Permet aux modals/sheets de s'enregistrer comme "back handlers"
 * Priorité : Handler enregistré → Navigation arrière → Exit app
 */

import { create } from 'zustand';

export type BackHandlerCallback = () => boolean; // Return true if handled

export interface HistoryEntry {
  action: 'register' | 'unregister';
  id: string;
  timestamp: number;
}

export interface BackHandlerStats {
  totalRegisters: number;
  totalUnregisters: number;
  operationsPerMinute: number;
  lastOperationTime: number;
}

interface BackHandlerState {
  handlers: Map<string, BackHandlerCallback>;
  history: HistoryEntry[];
  stats: BackHandlerStats;
  
  // Actions
  registerHandler: (id: string, callback: BackHandlerCallback) => void;
  unregisterHandler: (id: string) => void;
  executeTopHandler: () => boolean;
  hasHandlers: () => boolean;
  clear: () => void;
  resetStats: () => void;
}

const MAX_HISTORY_LENGTH = 100;

const calculateOpsPerMinute = (history: HistoryEntry[]): number => {
  const now = Date.now();
  const oneMinuteAgo = now - 60000;
  const recentOps = history.filter(h => h.timestamp > oneMinuteAgo);
  return recentOps.length;
};

export const useBackHandlerStore = create<BackHandlerState>((set, get) => ({
  handlers: new Map(),
  history: [],
  stats: {
    totalRegisters: 0,
    totalUnregisters: 0,
    operationsPerMinute: 0,
    lastOperationTime: 0,
  },

  registerHandler: (id: string, callback: BackHandlerCallback) => {
    const timestamp = Date.now();
    set((state) => {
      const newHandlers = new Map(state.handlers);
      newHandlers.set(id, callback);
      
      const newHistory = [
        ...state.history.slice(-MAX_HISTORY_LENGTH + 1),
        { action: 'register' as const, id, timestamp }
      ];
      
      return { 
        handlers: newHandlers,
        history: newHistory,
        stats: {
          ...state.stats,
          totalRegisters: state.stats.totalRegisters + 1,
          operationsPerMinute: calculateOpsPerMinute(newHistory),
          lastOperationTime: timestamp,
        }
      };
    });
  },

  unregisterHandler: (id: string) => {
    const currentHandlers = get().handlers;
    if (!currentHandlers.has(id)) return;
    
    const timestamp = Date.now();
    set((state) => {
      const newHandlers = new Map(state.handlers);
      newHandlers.delete(id);
      
      const newHistory = [
        ...state.history.slice(-MAX_HISTORY_LENGTH + 1),
        { action: 'unregister' as const, id, timestamp }
      ];
      
      return { 
        handlers: newHandlers,
        history: newHistory,
        stats: {
          ...state.stats,
          totalUnregisters: state.stats.totalUnregisters + 1,
          operationsPerMinute: calculateOpsPerMinute(newHistory),
          lastOperationTime: timestamp,
        }
      };
    });
  },

  executeTopHandler: () => {
    const { handlers } = get();
    if (handlers.size === 0) return false;

    // Exécuter le dernier handler enregistré (LIFO - Last In First Out)
    const handlersArray = Array.from(handlers.entries());
    const [, lastHandler] = handlersArray[handlersArray.length - 1];
    
    try {
      return lastHandler();
    } catch (error) {
      console.warn('[BackHandler] Error executing handler:', error);
      return false;
    }
  },

  hasHandlers: () => {
    return get().handlers.size > 0;
  },

  clear: () => {
    set({ handlers: new Map() });
  },
  
  resetStats: () => {
    set({
      history: [],
      stats: {
        totalRegisters: 0,
        totalUnregisters: 0,
        operationsPerMinute: 0,
        lastOperationTime: 0,
      }
    });
  },
}))
